import axios from 'axios';
import { useAuth } from 'contexts/auth';
import { useWebsocket } from 'contexts/websocket';
import { omit } from 'lodash';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import assignmentService from 'services/assignment';
import courseService from 'services/course';
import rubricService from 'services/rubric';
import userService from 'services/user';

const AppContext = createContext();
const useApp = () => useContext(AppContext);

const mergeAccountsAndUsers = (accounts, users) => {
    return accounts
        .filter(({ active }) => active)
        .map((account) => {
            const user = users.find(({ id }) => id === account.user_id);
            const userAccounts = accounts.filter(({ user_id }) => user_id === user.id);
            const isPending = userAccounts.some(({ login_activity }) => Boolean(login_activity));
            return {
                ...omit(account, ['_id', 'id']),
                ...omit(user, ['_id', 'id']),
                owner_account_id: account.id,
                user_id: user.id,
                is_pending: isPending,
            };
        });
};

const AppProvider = (props) => {
    const { auth, logout } = useAuth();
    const location = useLocation();
    const { setPingPayload, onMessage } = useWebsocket();

    // default value null means uninitialized unless stated otherwise
    const [course, setCourse] = useState(null);
    const [accountUsers, setAccountUsers] = useState(null); // account document and user document merged
    const [assignments, setAssignments] = useState(null);
    const [currentAssignmentId, setCurrentAssignmentId] = useState(null); // the assignment you are currently looking at
    const [batch, setBatch] = useState(null); // ongoing backend jobs for current assignment
    const [feedback, setFeedback] = useState(null); // feedback for current assignment and current accountUser
    const [submissions, setSubmissions] = useState(null); // submissions for current assignment
    const [previewSubmissions, setPreviewSubmissions] = useState(null); // preview submissions for current assignment, used for fine-tuning
    const [rubrics, setRubrics] = useState(null);
    const [customer, setCustomer] = useState(); // stripe customer, undefined means uninitialized, null means no customer
    const [subscription, setSubscription] = useState(); // stripe subscription, undefined means uninitialized, null means no subscription
    const [entitlements, setEntitlements] = useState(null); // stripe entitlements

    const isLoading = !course || !accountUsers || !assignments || typeof customer === 'undefined' || !entitlements;
    const instructorAccountUsers = useMemo(
        () => accountUsers?.filter(({ role }) => role === 'instructor'),
        [accountUsers]
    );

    const currentAssignment = assignments?.find(({ id }) => id === currentAssignmentId);

    const updateCourse = async (courseData) => {
        try {
            await courseService.updateCourse(auth.course_id, courseData);
            setCourse((prev) => ({ ...prev, ...courseData }));
            return true;
        } catch (e) {
            console.error(e);
            return false;
        }
    };

    const addAccountUser = (accountUser) => {
        setAccountUsers((current) => [...current, accountUser]);
    };

    const removeAccountUser = (accountUser) => {
        setAccountUsers((current) => {
            return current.filter(({ owner_account_id }) => owner_account_id !== accountUser.owner_account_id);
        });
    };

    useEffect(() => {
        if (!auth?.course_id) return setCourse(null);
        (async () => {
            const course = await courseService.findCourse(auth.course_id);
            setCourse(course);
        })();
    }, [auth?.course_id]);

    useEffect(() => {
        if (!course) return;
        (async () => {
            const [accounts, users, assignments] = await Promise.all([
                await courseService.listCourseAccounts(course.id),
                await courseService.listCourseUsers(course.id),
                await courseService.listCourseAssignments(course.id),
            ]);
            setAccountUsers(mergeAccountsAndUsers(accounts, users));
            setAssignments(assignments);
        })();
    }, [course]);

    useEffect(() => {
        if (location.pathname.startsWith('/assignments')) {
            const assignmentId = location.pathname.split('/')[2];
            if (!assignmentId) {
                setCurrentAssignmentId(null);
            }
            setCurrentAssignmentId(assignmentId);
        } else {
            setCurrentAssignmentId(null);
        }
    }, [location.pathname]);

    useEffect(() => {
        setBatch(null);
        setFeedback(null);
        setSubmissions(null);
        setPreviewSubmissions(null);

        if (!auth || !currentAssignmentId || !instructorAccountUsers) return;

        const controller = new AbortController();
        const signal = controller.signal;

        const fetchData = async () => {
            try {
                const instructorOwnerAccountIds = instructorAccountUsers.map(
                    ({ owner_account_id }) => owner_account_id
                );

                const [batch, feedback, submissions, previewSubmissions] = await Promise.all([
                    assignmentService.listAssignmentBatch(
                        currentAssignmentId,
                        JSON.stringify({
                            is_finished: false,
                            owner_account_id: instructorOwnerAccountIds,
                        }),
                        null,
                        null,
                        null,
                        { signal }
                    ),
                    assignmentService.listAssignmentFeedback(currentAssignmentId, null, null, null, null, { signal }),
                    assignmentService.listAssignmentSubmissions(
                        currentAssignmentId,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        { signal }
                    ),
                    assignmentService.listAssignmentSubmissions(
                        currentAssignmentId,
                        JSON.stringify({ type: 'fine-tune' }),
                        null,
                        null,
                        null,
                        { signal }
                    ),
                ]);

                setBatch(batch);
                setFeedback(feedback);
                setSubmissions(
                    submissions.map((submission) => {
                        if (!submission.files) return { ...submission, files: [submission.data] };
                        return submission;
                    })
                );
                setPreviewSubmissions(
                    previewSubmissions.map((ps) => {
                        if (!ps.files) return { ...ps, files: [ps.data] };
                        return ps;
                    })
                );
            } catch (error) {
                if (axios.isCancel?.(error) || error.name === 'CanceledError') {
                    // request was cancelled
                } else {
                    console.error(error);
                }
            }
        };

        fetchData();

        return () => controller.abort();
    }, [currentAssignmentId, auth, instructorAccountUsers]);

    useEffect(() => {
        const cleanup = setPingPayload(() => {
            if (!currentAssignmentId || !batch) return {};
            return { batch_ids: batch.map(({ id }) => id) };
        });
        return cleanup;
    }, [currentAssignmentId, batch, setPingPayload]);

    useEffect(() => {
        const cleanup = onMessage(async (message) => {
            if (message.type !== 'pong' || !message.batches) return;

            const isAlreadyUpToDate = (batch, list) => {
                const currentCount = list?.filter(({ batch_id }) => batch_id === batch.id).length;
                return currentCount >= batch.submission_count;
            };

            const handleFineTuneBatch = async () => {
                const previewSubmissions = await assignmentService.listAssignmentSubmissions(
                    currentAssignmentId,
                    JSON.stringify({ type: 'fine-tune' })
                );
                setPreviewSubmissions(
                    previewSubmissions.map((ps) => {
                        if (!ps.files) return { ...ps, files: [ps.data] };
                        return ps;
                    })
                );
                setAssignments((current) => {
                    return current.map((assignment) => {
                        if (assignment.id !== currentAssignmentId) return assignment;
                        return {
                            ...assignment,
                            preview_files: assignment.preview_files.map((pf) => {
                                const matched = previewSubmissions.find(
                                    (ps) =>
                                        ps.files?.[0]?.key === pf.data.key ||
                                        ps.files?.[0]?.key?.includes(pf.data.key) ||
                                        ps.data?.key === pf.data.key ||
                                        ps.data?.key?.includes(pf.data.key)
                                );
                                return matched ? { ...pf, is_preview_up_to_date: true } : pf;
                            }),
                        };
                    });
                });
            };

            const handleRegularBatch = async () => {
                // support multi-file backward compatibility
                const mapDataToFiles = (arr) => {
                    return arr.map((item) => {
                        if (!item.files) {
                            item.files = [item.data];
                        }
                        return item;
                    });
                };

                const newSubmissions = await assignmentService.listAssignmentSubmissions(currentAssignmentId);

                setSubmissions((current) => {
                    const additionalSubmissions = newSubmissions.filter((item) => {
                        if (current.map(({ id }) => id).includes(item.id)) return false;
                        return true;
                    });
                    const submissionsAfterFilteringRemoved = (current ?? []).filter((item) => {
                        if (!newSubmissions.map(({ id }) => id).includes(item.id)) return false;
                        return true;
                    });
                    const final = mapDataToFiles([...submissionsAfterFilteringRemoved, ...additionalSubmissions]);
                    return final;
                });
            };

            await Promise.all(
                Object.values(message.batches).map(async (batch) => {
                    if (batch.type === 'fine-tune' && isAlreadyUpToDate(batch, previewSubmissions)) return;
                    if (!batch.type && isAlreadyUpToDate(batch, submissions)) return;

                    if (batch.type === 'fine-tune') {
                        await handleFineTuneBatch(batch);
                    } else {
                        await handleRegularBatch(batch);
                    }

                    if (batch.is_finished) {
                        setBatch((current) => current.filter((b) => b.id !== batch.id));
                    }
                })
            );
        });

        return cleanup;
    }, [currentAssignmentId, onMessage, previewSubmissions, submissions]);

    useEffect(() => {
        if (!auth?.user_id) return;
        (async () => {
            const rubrics = await rubricService.listRubrics(
                JSON.stringify({
                    user_id: auth.user_id,
                })
            );
            setRubrics(rubrics);
        })();
    }, [auth?.user_id]);

    useEffect(() => {
        if (!auth?.user_id) return;
        (async () => {
            if (typeof customer === 'undefined') {
                const customers = await userService.getUserCustomer(auth.user_id);
                if (customers?.length === 0) {
                    await logout({ isExpired: true });
                } else if (customers?.length === 1) {
                    setCustomer(customers[0]);
                }
            }
        })();
    }, [auth?.user_id, customer, logout]);

    useEffect(() => {
        if (!auth?.user_id) return;

        if (!customer) {
            setSubscription(null);
            setEntitlements([]);
            return;
        }

        (async () => {
            const subscription = await userService.getUserSubscriptions(auth.user_id, customer.id);
            setSubscription(subscription);
        })();
        (async () => {
            const entitlements = await userService.getUserEntitlements(auth.user_id, customer.id);
            setEntitlements(entitlements);
        })();
    }, [auth?.user_id, customer]);

    return (
        <AppContext.Provider
            {...props}
            value={{
                isLoading,

                // core data
                course,
                accountUsers,
                setAccountUsers,
                instructorAccountUsers, // accountUsers with role 'instructor'
                assignments,
                setAssignments,

                // assignment related data
                currentAssignmentId,
                setCurrentAssignmentId,
                currentAssignment,
                batch,
                setBatch,
                feedback,
                setFeedback,
                submissions,
                setSubmissions,
                previewSubmissions,
                setPreviewSubmissions,
                rubrics,
                setRubrics,

                // stripe
                customer,
                subscription,
                setSubscription,
                entitlements,
                setEntitlements,

                updateCourse,
                addAccountUser,
                removeAccountUser,
            }}
        />
    );
};

export { useApp };
export default AppProvider;
